import { API_BASE_URL } from '../config/api';
import { apiGet, apiPatch, apiPost } from '../utils/apiClient';
import { loadUser } from '../utils/storage';
import { invalidateMyPageCache } from './mypageService';

export interface AuthUser {
  email: string;
  userKey?: number; // user_key (서버에서 제공)
  nickname: string;
  balance: number;
  gameCount: number;
}

export interface AuthResult {
  user: AuthUser;
  accessToken: string | null;
  refreshToken: string | null;
}

const normalizeAuthResponse = (payload: any): AuthResult => {
  const visited = new Set<any>();
  const queue: any[] = [];

  if (payload && typeof payload === 'object') {
    queue.push(payload);
  }

  let rawUser: any = null;
  let accessToken: string | null = null;
  let refreshToken: string | null = null;

  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || visited.has(current)) {
      continue;
    }

    visited.add(current);

    const currentUser = current.user;
    if (!rawUser && currentUser && typeof currentUser === 'object') {
      rawUser = currentUser;
    }

    if (!accessToken && typeof current.accessToken === 'string') {
      accessToken = current.accessToken;
    }

    if (!refreshToken && typeof current.refreshToken === 'string') {
      refreshToken = current.refreshToken;
    }

    if (!accessToken && typeof current.token === 'string') {
      accessToken = current.token;
    }

    Object.values(current).forEach(value => {
      if (value && typeof value === 'object' && !visited.has(value)) {
        queue.push(value);
      }
    });

    if (rawUser && accessToken && refreshToken) {
      break;
    }
  }

  if (!rawUser) {
    console.error('❗️인증 응답 파싱 실패:', payload);
    throw new Error('서버에서 사용자 정보를 받지 못했습니다.');
  }

  const normalizedUser: AuthUser = {
    ...rawUser,
    userKey: rawUser.userKey ?? rawUser.user_key,
    balance: Number(
      rawUser.balance ?? rawUser.money ?? rawUser?.profile?.balance ?? 0
    ),
    gameCount: Number(rawUser.gameCount ?? rawUser?.stats?.gameCount ?? 0),
  };

  return { user: normalizedUser, accessToken, refreshToken };
};

export const login = async (
  email: string,
  password: string
): Promise<AuthResult> => {
  // apiClient를 사용하여 백엔드의 공통 응답 구조를 자동으로 처리
  // apiPost는 ResponseInterceptor가 래핑한 응답에서 자동으로 data를 추출합니다.
  const data = await apiPost<any>(
    `${API_BASE_URL}/api/auth/login`,
    { email, password },
    { skipAuth: true }
  );

  // normalizeAuthResponse가 중첩된 구조를 처리하므로 data.data 또는 data 모두 처리 가능
  return normalizeAuthResponse(data);
};

export const register = async (
  email: string,
  password: string,
  nickname: string
): Promise<AuthResult> => {
  // apiClient를 사용하여 백엔드의 공통 응답 구조를 자동으로 처리
  const data = await apiPost<any>(
    `${API_BASE_URL}/api/auth/register`,
    { email, password, nickname },
    { skipAuth: true }
  );

  return normalizeAuthResponse(data);
};

export const refresh = async (refreshToken: string): Promise<AuthResult> => {
  // apiClient를 사용하여 백엔드의 공통 응답 구조를 자동으로 처리
  const data = await apiPost<any>(
    `${API_BASE_URL}/api/auth/refresh`,
    { refreshToken },
    { skipAuth: true }
  );

  return normalizeAuthResponse(data);
};

/**
 * 카카오 로그인 URL 가져오기
 */
export const getKakaoAuthUrl = async (): Promise<string> => {
  const data = await apiGet<any>(`${API_BASE_URL}/api/auth/kakao`, {
    skipAuth: true,
  });

  // 응답에서 URL 추출
  if (data?.data?.url) {
    return data.data.url;
  }
  if (data?.url) {
    return data.url;
  }

  throw new Error('카카오 로그인 URL을 가져올 수 없습니다.');
};

/**
 * 카카오 로그인 콜백 처리
 */
export const kakaoLogin = async (code: string): Promise<any> => {
  const data = await apiGet<any>(
    `${API_BASE_URL}/api/auth/kakao/callback?code=${code}`,
    { skipAuth: true }
  );

  // 원본 응답 반환 (needsConsent 체크를 위해)
  return data;
};

/**
 * 닉네임 변경
 */
export const updateNickname = async (nickname: string): Promise<AuthUser> => {
  try {
    // apiPatch는 이미 responseData.data를 반환하므로, 직접 { userKey, nickname, canChangeNicknameAt } 형태로 받음
    const data = await apiPatch<{
      userKey: number;
      nickname: string;
      canChangeNicknameAt?: string | null;
    }>(`${API_BASE_URL}/api/user/nickname`, {
      nickname,
    });

    // 응답에서 사용자 정보 추출
    if (data?.userKey && data?.nickname) {
      // 닉네임 변경 시 마이페이지 캐시 무효화
      invalidateMyPageCache();
      
      // 기존 사용자 정보를 로드하여 email 유지
      const existingUser = loadUser<AuthUser>();
      
      return {
        email: existingUser?.email || '', // 기존 email 유지
        userKey: data.userKey,
        nickname: data.nickname,
        balance: existingUser?.balance || 0, // 기존 balance 유지
        gameCount: existingUser?.gameCount || 0, // 기존 gameCount 유지
      };
    }

    throw new Error('닉네임 변경 응답 형식이 올바르지 않습니다.');
  } catch (error: any) {
    // apiRequest에서 throw된 Error 객체의 message에 서버 에러 메시지가 들어있음
    const errorMessage = error?.message || '닉네임 변경에 실패했습니다.';
    throw new Error(errorMessage);
  }
};

/**
 * 비밀번호 재설정 요청 (이메일 발송)
 */
export const forgotPassword = async (email: string): Promise<void> => {
  await apiPost(
    `${API_BASE_URL}/api/auth/forgot-password`,
    { email },
    { skipAuth: true }
  );
};

/**
 * 비밀번호 재설정 (토큰으로 새 비밀번호 설정)
 */
export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<void> => {
  await apiPost(
    `${API_BASE_URL}/api/auth/reset-password`,
    { token, newPassword },
    { skipAuth: true }
  );
};

/**
 * 카카오 신규 가입 완료 (동의 후 실제 가입)
 */
export const completeKakaoSignup = async (
  tempToken: string,
  agreedToPrivacy: boolean,
  agreedToTerms: boolean
): Promise<AuthResult> => {
  const response = await apiPost(
    `${API_BASE_URL}/api/auth/complete-signup`,
    {
      tempToken,
      agreedToPrivacy,
      agreedToTerms,
    },
    { skipAuth: true }
  );

  return normalizeAuthResponse(response);
};
